function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '=',
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function isWebAuthnSupported() {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined'
  );
}

export async function startRegistration(options) {
  const {
    sessionId: _sessionId,
    challenge,
    user,
    excludeCredentials,
    ...rest
  } = options;

  const publicKey = {
    ...rest,
    challenge: base64urlToBuffer(challenge),
    user: {
      ...user,
      id: base64urlToBuffer(user.id),
    },
  };

  if (excludeCredentials) {
    publicKey.excludeCredentials = excludeCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }));
  }

  const credential = await navigator.credentials.create({ publicKey });

  if (!credential) {
    throw new Error('Registration ceremony returned no credential.');
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(
        credential.response.attestationObject,
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  };
}

export async function isConditionalMediationAvailable() {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential !== 'undefined' &&
      typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
      (await window.PublicKeyCredential.isConditionalMediationAvailable())
    );
  } catch {
    return false;
  }
}

export async function startConditionalAuthentication(options, abortSignal) {
  const {
    sessionId,
    challenge,
    allowCredentials,
    rpId,
    userVerification,
  } = options;

  const publicKey = {
    challenge: base64urlToBuffer(challenge),
    ...(rpId && { rpId }),
    ...(userVerification && { userVerification }),
  };

  if (allowCredentials) {
    publicKey.allowCredentials = allowCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }));
  }

  const credentialRequestOptions = { publicKey, mediation: 'conditional' };
  if (abortSignal) {
    credentialRequestOptions.signal = abortSignal;
  }

  const credential = await navigator.credentials.get(credentialRequestOptions);

  if (!credential) return null;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    sessionId,
    response: {
      authenticatorData: bufferToBase64url(
        credential.response.authenticatorData,
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : null,
    },
  };
}

export async function startAuthentication(options) {
  const {
    sessionId,
    challenge,
    allowCredentials,
    timeout,
    rpId,
    userVerification,
  } = options;

  const publicKey = {
    challenge: base64urlToBuffer(challenge),
    ...(timeout && { timeout }),
    ...(rpId && { rpId }),
    ...(userVerification && { userVerification }),
  };

  if (allowCredentials) {
    publicKey.allowCredentials = allowCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }));
  }

  const credential = await navigator.credentials.get({ publicKey });

  if (!credential) {
    throw new Error('Authentication ceremony returned no credential.');
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    sessionId,
    response: {
      authenticatorData: bufferToBase64url(
        credential.response.authenticatorData,
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : null,
    },
  };
}
